import { message } from 'antd'
import { type AxiosError, type AxiosResponse } from 'axios'
import {
  useMutation,
  useQueryClient,
  type UseMutationResult
} from '@tanstack/react-query'
import { adminApi, genericApi } from '~/api'
import { clipboard, showErrors } from '~/utils/helpers'
import { courseParticipantQueryKey } from './query'
import { apiResetParticipantPasskey } from '~/api/adminApi'

declare type ApiMutationResult = UseMutationResult<
  AxiosResponse<{ data: any }>,
  AxiosError<{ message: string }>,
  any
>

export const useSendAllLoginDetailMutation = (
  courseId: number
): ApiMutationResult =>
  useMutation(
    async () => {
      const { data } = await adminApi.sendAllLoginDetails(courseId)
      return data.data
    },
    {
      onSuccess: () => {
        message.success("Student's login details has been sent successfully")
      },
      onError: showErrors
    }
  )

export const useSendStudentLoginDetailMutation = (
  courseId: number
): ApiMutationResult =>
  useMutation(
    async ({ studentId, testing }) => {
      const { data } = await adminApi.sendStudentLoginDetails(
        courseId,
        studentId,
        testing
      )
      return data.data
    },
    {
      onSuccess: () => {
        message.success("Student's login details has been sent successfully")
      },
      onError: showErrors
    }
  )

export const useImportStudentMutation = (
  courseId: number
): ApiMutationResult => {
  const queryClient = useQueryClient()

  return useMutation(
    async (payload) => await adminApi.importStudent(courseId, payload.file),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: courseParticipantQueryKey(courseId) })
        message.success('Import successfully')
      },
      onError: showErrors
    }
  )
}

export const useCreateStudentMutation = (
  courseId: number
): ApiMutationResult => {
  const queryClient = useQueryClient()
  return useMutation(
    async (payload) => {
      const { data } = await adminApi.createStudent(courseId, payload)
      return data.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: courseParticipantQueryKey(courseId) })
        message.success('Success')
      },
      onError: showErrors
    }
  )
}

export const useEditStudentMutation = (
  courseId,
  studentId
): ApiMutationResult => {
  return useMutation(
    async (payload) => {
      const { data } = await adminApi.editStudent(courseId, studentId, payload)
      return data.data
    },
    {
      onSuccess: async () => {
        message.success('Success')
      },
      onError: showErrors
    }
  )
}

export const useStudentEnabledMutation = (
  courseId: number
): ApiMutationResult => {
  const queryClient = useQueryClient()
  return useMutation(
    async (studentId) => {
      const { data } = await adminApi.editStudentEnabled(courseId, studentId)
      return data.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['courses', courseId] })
        message.success('Success')
      },
      onError: showErrors
    }
  )
}

export const useStudentPreAccessMutation = (
  courseId: number
): ApiMutationResult => {
  const queryClient = useQueryClient()
  return useMutation(
    async (studentId) => {
      const { data } = await adminApi.editStudentPreAccess(courseId, studentId)
      return data.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['courses', courseId] })
        message.success('Success')
      },
      onError: showErrors
    }
  )
}

export const useStudentPasskeyRequiredMutation = (
  courseId: number
): ApiMutationResult => {
  const queryClient = useQueryClient()
  return useMutation(
    async (studentId) => {
      const { data } = await adminApi.editStudentPasskeyRequired(
        courseId,
        studentId
      )
      return data.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['courses', courseId] })
        message.success('Success')
      },
      onError: showErrors
    }
  )
}

export const useNativeAppstreamLinkMutation = (
  courseId: number
): ApiMutationResult =>
  useMutation(
    async (studentId) => {
      const { data } = await adminApi.getNativeAppstreamLink(
        courseId,
        studentId
      )
      return data.data
    },
    {
      onSuccess: (data) => {
        clipboard(data)
        message.success('Copied')
      },
      onError: showErrors
    }
  )

export const useDeleteparticipantMutation = (id): ApiMutationResult => {
  const queryClient = useQueryClient()
  return useMutation(
    async (studentId) => {
      const { data } = await genericApi.deleteParticipant(studentId)
      return data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: courseParticipantQueryKey(id) })
        message.success('Success')
      },
      onError: showErrors
    }
  )
}

export const useResetPasskeyMutation = (
  courseId: number
): ApiMutationResult => {
  const queryClient = useQueryClient()
  return useMutation(
    async (studentId) => {
      const { data } = await apiResetParticipantPasskey(studentId)
      return data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: courseParticipantQueryKey(courseId) })
        message.success('Passkey reset successfully')
      },
      onError: showErrors
    }
  )
}
